import { formatCurrency } from "src/shared/utils/formatCurrency"
import { BudgetChange, PauseAsk, PauseScope } from "./useBudgetDraft"
import { fmtDay, pct2 } from "./segmentEngine"

/**
 * Always-on before/after confirm for anything that moves money. There is no size threshold:
 * what the 9/24 review asked for was visibility of the change before it saves, not a
 * magnitude gate.
 */
export function BudgetConfirmDialog({
  margin,
  segments,
  onCancel,
  onConfirm,
}: {
  margin: { from: number; to: number; amountFrom: number; amountTo: number } | null
  segments: BudgetChange[]
  onCancel: () => void
  onConfirm: () => void
}) {
  const title =
    margin && segments.length
      ? "Confirm margin and budget changes"
      : margin
        ? "Confirm margin change"
        : "Confirm budget change"

  const val = (v: number | null) => (v == null ? <span style={{ color: "#94a3b8" }}>—</span> : formatCurrency(v))

  const row = (label: string, from: React.ReactNode, to: React.ReactNode, key: string) => (
    <div className="seg-dialog__row" key={key}>
      <span className="seg-dialog__row-label">{label}</span>
      <span className="seg-dialog__row-move">
        {from}
        <span className="seg-dialog__arrow"> → </span>
        {to}
      </span>
    </div>
  )

  return (
    <div className="seg-dialog" onClick={onCancel}>
      <div
        className="seg-dialog__panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="seg-dialog__title">{title}</div>

        {margin && (
          <>
            <div className="seg-dialog__group">Margin</div>
            {row("Margin", `${pct2(margin.from)}%`, `${pct2(margin.to)}%`, "m")}
            {row("Amount", formatCurrency(margin.amountFrom), formatCurrency(margin.amountTo), "a")}
          </>
        )}

        {segments.length > 0 && (
          <>
            <div className="seg-dialog__group">
              {segments.length === 1 ? "Budget" : `Budget · ${segments.length} segments`}
            </div>
            {segments.map((s, i) => row(s.label, val(s.from), val(s.to), String(i)))}
          </>
        )}

        <div className="seg-dialog__actions">
          <button type="button" className="seg-btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="seg-btn seg-btn--primary" onClick={onConfirm}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Three explicit scopes rather than Yes/No: "Yes" used to read as rest-of-flight while it
 * only touched the current period, and those are two outcomes Ops needs to tell apart at the
 * moment of the decision.
 */
export function PauseConfirmDialog({
  pauses,
  step,
  flightEnd,
  onCancel,
  onChoose,
}: {
  pauses: PauseAsk[]
  step: string | null
  flightEnd: string
  onCancel: () => void
  onChoose: (c: PauseScope) => void
}) {
  const allEven = pauses.every((p) => p.even)
  const one = pauses.length === 1 ? pauses[0] : null
  const period = allEven
    ? "the whole flight (Even has one segment)"
    : one
      ? one.scope.replace(/ only$/, "")
      : "each paused segment"

  /*
      With nothing left to reallocate to, the two reallocation buttons would do exactly what
      Leave unallocated does, so offering them would be a choice between three labels and one
      outcome (10/8). They are dropped and the reason is stated instead.
  */
  const nowhereToGo = pauses.every((p) => p.reallocTo.length === 0)

  const choices: { k: PauseScope; label: string; sub: string }[] = [
    {
      k: "none",
      label: "Leave unallocated",
      sub: "No reallocation. The paused share sits unallocated while the network is off.",
    },
    {
      k: "period",
      label: "Reallocate for this period only",
      sub: `Spreads it across the other active networks for ${period}. Later segments are unchanged.`,
    },
    {
      k: "flight",
      label: "Reallocate for remainder of flight",
      sub: `Pauses it and reallocates in this and every later segment through ${fmtDay(flightEnd)}.`,
    },
  ]
  const options = choices.filter((o) => !nowhereToGo || o.k === "none")

  return (
    <div className="seg-dialog" onClick={onCancel}>
      <div
        className="seg-dialog__panel seg-dialog__panel--wide"
        role="dialog"
        aria-modal="true"
        aria-label="Reallocate the paused network's share?"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="seg-dialog__title">
          Reallocate the paused network&apos;s share?
          {step && <span className="seg-dialog__step">{step}</span>}
        </div>

        {pauses.map((p, i) => (
          <div className="seg-dialog__body" key={i}>
            {p.kind === "ag" ? (
              <>
                <strong>{p.names}</strong> will have no active ad groups ({p.reason}), freeing
              </>
            ) : (
              <>
                Pausing <strong>{p.names}</strong> frees
              </>
            )}{" "}
            <strong>
              {pct2(p.pct)}% ({formatCurrency(p.amount)})
            </strong>{" "}
            for the {p.scope}.
            {p.reallocTo.length === 0 && (
              <div className="seg-dialog__note">
                {p.kind === "ag"
                  ? "No other networks run this ad group, so its share stays unallocated."
                  : "No other networks are active, so the share stays unallocated."}
              </div>
            )}
          </div>
        ))}

        <div className="seg-dialog__options">
          {options.map((o) => (
            <button key={o.k} type="button" className="seg-choice" onClick={() => onChoose(o.k)}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="seg-choice__label">{o.label}</span>
                <span className="seg-choice__sub">{o.sub}</span>
              </span>
              <span className="seg-choice__chev">›</span>
            </button>
          ))}
        </div>

        <div className="seg-dialog__actions">
          <button type="button" className="seg-btn" onClick={onCancel}>
            Keep editing
          </button>
        </div>
      </div>
    </div>
  )
}
